import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Menu, Phone, MessageCircle, MapPin, Clock, Video, CreditCard, Shield, Car, ShoppingCart, FileText, Wrench, Heart, Search, Users, Package, Settings, Gauge, Handshake, HandCoins, X, Navigation, ChevronDown, Layout } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Link } from 'react-router-dom';
import { ConfigModal } from '@/components/ConfigModal';
import { ServiceModal } from '@/components/ServiceModal';
import { StoreSelectionModal } from '@/components/StoreSelectionModal';
import { LayoutSelectionModal } from '@/components/LayoutSelectionModal';
import { AccountIdModal } from '@/components/AccountIdModal';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { openWhatsApp } from '@/lib/whatsapp';
import { getAccountId } from '@/lib/obfuscate';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { getStoreBrandAssets } from '@/lib/storeBrand';
import { HeaderBrandLogo } from '@/components/HeaderBrandLogo';

// Import service images
import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';

interface HeaderProps {
  config?: any;
}

export const Header = ({ config: propConfig }: HeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isLayoutModalOpen, setIsLayoutModalOpen] = useState(false);
  const [isAccountIdModalOpen, setIsAccountIdModalOpen] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const { config, vehicles, accountId } = useAppData();
  const { colors } = useTheme();
  const { selectedStoreIndex, setSelectedStoreIndex, availableStores, selectedStore } = useStore();

  // Get original account ID from environment (for button visibility check)
  const originalAccountId = getAccountId();
  const shouldShowDevButtons = originalAccountId === '677e7e1b763f79265b9228db';

  // Use context config if no prop config is provided
  const currentConfig = propConfig || config;

  // Use selected store from context
  const store = selectedStore || currentConfig.stores[0];

  // Check if should show store selector (more than 1 available store)
  const showStoreSelector = availableStores.length > 1;

  // Update favorites count - only count vehicles that still exist
  useEffect(() => {
    const updateFavoritesCount = async () => {
      try {
        const favoriteIds = await secureGetItem('favorites');

        if (favoriteIds && vehicles.length > 0 && Array.isArray(favoriteIds)) {
          // Filter to only count favorites that still exist in the vehicle list
          const existingFavorites = favoriteIds.filter(id =>
            vehicles.some(v => v.id === id)
          );

          setFavoritesCount(existingFavorites.length);

          // Clean up localStorage if there are sold vehicles
          if (existingFavorites.length !== favoriteIds.length) {
            await secureSetItem('favorites', existingFavorites);
          }
        } else {
          setFavoritesCount(0);
        }
      } catch (error) {
        setFavoritesCount(0);
      }
    };

    // Initial count
    updateFavoritesCount();

    // Listen for favorites updates
    window.addEventListener('favoritesUpdated', updateFavoritesCount);
    window.addEventListener('storage', updateFavoritesCount);

    return () => {
      window.removeEventListener('favoritesUpdated', updateFavoritesCount);
      window.removeEventListener('storage', updateFavoritesCount);
    };
  }, [vehicles]);

  const headerColor = colors.header;
  const menuColor = colors.menu;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const buttonColor = colors.button;

  // Format opening hours for display in header - groups consecutive days with same hours
  const getFormattedHours = () => {
    const hours = store?.openingHours?.default;
    if (!hours) return 'Seg-Sex: 8h-19h | Sáb: 8h-18h';

    const daysOrder = [
      { key: 'monday', short: 'Seg' },
      { key: 'tuesday', short: 'Ter' },
      { key: 'wednesday', short: 'Qua' },
      { key: 'thursday', short: 'Qui' },
      { key: 'friday', short: 'Sex' },
      { key: 'saturday', short: 'Sáb' },
      { key: 'sunday', short: 'Dom' }
    ];

    // Group consecutive days with same hours
    const groups: Array<{ days: string[]; time: string }> = [];
    let currentGroup: { days: string[]; time: string } | null = null;

    daysOrder.forEach((day) => {
      const dayHours = hours[day.key];
      const timeStr = dayHours?.from && dayHours?.to
        ? `${dayHours.from}-${dayHours.to}`
        : 'Fechado';

      if (!currentGroup) {
        // Start new group
        currentGroup = { days: [day.short], time: timeStr };
      } else if (currentGroup.time === timeStr) {
        // Same hours - add to current group
        currentGroup.days.push(day.short);
      } else {
        // Different hours - save current group and start new one
        groups.push(currentGroup);
        currentGroup = { days: [day.short], time: timeStr };
      }
    });

    // Add last group
    if (currentGroup) {
      groups.push(currentGroup);
    }

    // Format groups for display (limit to 3 groups for header space)
    const formattedGroups = groups.slice(0, 3).map(group => {
      const dayRange = group.days.length > 1
        ? `${group.days[0]} à ${group.days[group.days.length - 1]}`
        : group.days[0];
      return `${dayRange}: ${group.time}`;
    });

    if (formattedGroups.length === 0) {
      return 'Consulte horários';
    }

    return formattedGroups.join(' | ');
  };

  const formattedHours = getFormattedHours();

  // Request user location only when needed (user interaction)
  // Don't request automatically on page load to avoid trust issues

  // Get main service info
  const mainServiceId = store.mainService;
  const mainService = store.services?.find((s: any) => s.service === mainServiceId);
  const hasWebBanners = store.banners?.filter((v: any) => v.platform === 'mobile').length > 0;

  const serviceInfo: Record<string, { icon: any; title: string; description: string; image: string; color: string }> = {
    '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa', image: videoCall, color: 'bg-blue-500' },
    '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado', image: valuation, color: 'bg-green-500' },
    '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança', image: consignment, color: 'bg-purple-500' },
    '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições', image: consortium, color: 'bg-teal-500' },
    '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa', image: forwardingAgent, color: 'bg-orange-500' },
    '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado', image: financing, color: 'bg-blue-600' },
    '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo', image: partsAccessories, color: 'bg-gray-600' },
    '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada', image: scheduleReview, color: 'bg-slate-600' },
    '65dcb7238eca108d87c91dba': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país', image: insurance, color: 'bg-red-500' },
    '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo', image: scheduleServices, color: 'bg-indigo-600' },
    '65dcb7fe8eca108d87c91dbe': { icon: FileText, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE', image: fipeTable, color: 'bg-cyan-500' },
    '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos', image: testdrive, color: 'bg-indigo-500' },
    '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado', image: directSales, color: 'bg-green-600' }
  };

  const mainServiceInfo = mainServiceId ? serviceInfo[mainServiceId] : null;
  const MainServiceIcon = mainServiceInfo?.icon || MessageCircle;
  const mainServiceTitle = mainServiceInfo?.title || 'Fale Conosco';

  // Get service image (from config or default)
  const mainServiceImage = mainService?.image || mainServiceInfo?.image;

  const getServiceButtonText = (serviceId: string) => {
    const buttonTexts: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar',
      '65dceeac8eca108d87c91dc6': 'Agendar',
      '65dcef098eca108d87c91dc7': 'Agendar',
      '65dcb5e78eca108d87c91db8': 'Agendar',
      '65dcb7898eca108d87c91dbc': 'Simular',
      '65dcb7548eca108d87c91dbb': 'Avaliar',
      '65dcb7c58eca108d87c91dbd': 'Avaliar',
      '65dcb7238eca108d87c91dba': 'Cotar',
      '65dcb6528eca108d87c91db9': 'Cotar',
      '6628fd535bda2ae51f6fc5ff': 'Cotar',
      '65dcef468eca108d87c91dc8': 'Solicitar',
      '65dcb8548eca108d87c91dbf': 'Solicitar',
      '65dcb7fe8eca108d87c91dbe': 'Consultar',
    };
    return buttonTexts[serviceId] || 'Solicitar';
  };

  const menuItems = [
    { label: 'Início', href: '/', isLink: true },
    { label: 'Estoque', href: '/estoque', isLink: true },
    { label: 'Serviços', href: '/servicos', isLink: true },
    { label: 'Contato', href: '/contato', isLink: true }
  ];

  const { hasLogo, headerBrandSrc: brandImageSrc } = getStoreBrandAssets(currentConfig, store);

  const renderBrandMark = (variant: 'nav' | 'sheet' = 'nav') => {
    if (!brandImageSrc) return null;

    if (hasLogo) {
      return (
        <HeaderBrandLogo
          src={brandImageSrc}
          alt={currentConfig.name}
          variant={variant}
        />
      );
    }

    return (
      <div className="flex items-center justify-center shrink-0">
        <img
          src={brandImageSrc}
          alt={currentConfig.name}
          className={`object-contain rounded-xl ${variant === 'sheet' ? 'w-10 h-10' : 'w-10 h-10 lg:w-11 lg:h-11'}`}
        />
      </div>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md">
      {/* Info Bar */}
      <div>
        <div
          className="text-white py-1.5 md:py-2 px-2 md:px-4 h-[45px] flex items-center"
          style={{ backgroundColor: menuColor }}
        >
          <div className="container mx-auto flex items-center justify-between text-xs md:text-sm px-4"
            style={{ color: headerColor }}>
            {/* Mobile: Telefone + WhatsApp (Apenas Ícones) */}
            <div className="flex lg:hidden items-center gap-3 md:gap-4">
              <a
                href={store.phone?.ios}
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-all cursor-pointer"
                aria-label="Ligar"
              >
                <Phone className="h-4 w-4" />
              </a>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  openWhatsApp(store.whatsapp?.ios);
                }}
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-all"
                aria-label="WhatsApp"
              >
                <MessageCircle className="h-4 w-4" />
              </button>
            </div>

            {/* Desktop: Todas as informações */}
            <div className="hidden lg:flex items-center gap-6">

              {/* <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
                <span>{store?.place?.address || 'Endereço da empresa'}</span>
            </div> */}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{formattedHours}</span>
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-4">
              {/* Store Selector - Only show if multiple stores available */}
              {showStoreSelector && (
                <button
                  onClick={() => setIsStoreModalOpen(true)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-fast"
                >
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span className="text-xs truncate max-w-[150px]">{store.company || `Loja ${selectedStoreIndex + 1}`}</span>
                  <ChevronDown className="h-3 w-3 flex-shrink-0" />
                </button>
              )}

              <a
                href={store.phone?.ios}
                className="flex items-center gap-2 hover:text-accent transition-fast cursor-pointer"
                aria-label={store.phone?.formatted ? `Ligar para ${store.phone.formatted}` : 'Ligar para a loja'}
              >
                <Phone className="h-4 w-4" />
                <span>{store.phone?.formatted}</span>
              </a>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  openWhatsApp(store.whatsapp?.ios);
                }}
                className="flex items-center gap-2 hover:text-accent transition-fast"
                aria-label={store.whatsapp?.formatted ? `Conversar no WhatsApp ${store.whatsapp.formatted}` : 'Conversar no WhatsApp'}
              >
                <MessageCircle className="h-4 w-4" />
                <span>WhatsApp</span>
              </button>
            </div>

            {/* Mobile: Store Selector only */}
            <div className="flex lg:hidden items-center gap-1">
              {showStoreSelector && (
                <button
                  onClick={() => setIsStoreModalOpen(true)}
                  className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-fast"
                >
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span className="text-xs truncate max-w-[120px]">{store.company || `Loja ${selectedStoreIndex + 1}`}</span>
                  <ChevronDown className="h-3 w-3 flex-shrink-0" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Header */}

        <div
          className="flex items-center"
          style={{ backgroundColor: headerColor }}
        >
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2.5 min-w-0">
                {renderBrandMark('nav')}
                {!hasLogo && (
                  <div className="hidden sm:block min-w-0">
                    <h1 className="text-xl font-bold leading-tight truncate" style={{ color: menuColor }}>{currentConfig.name}</h1>
                    {currentConfig.slogan && (
                      <p className="hidden md:block text-sm truncate" style={{ color: menuColor, opacity: 0.75 }}>{currentConfig.slogan}</p>
                    )}
                  </div>
                )}
              </Link>

              {/* Desktop Navigation + Actions - Aligned Right */}
              <div className="hidden lg:flex items-center gap-4">
                <nav className="flex items-center space-x-8">
                  {menuItems.map((item) => (
                    item.isLink ? (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="text-sm text-foreground hover:text-primary transition-fast font-medium"
                        style={{ color: menuColor }}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <a
                        key={item.label}
                        href={item.href}
                        className="text-sm text-foreground hover:text-primary transition-fast font-medium"
                        style={{ color: menuColor }}
                      >
                        {item.label}
                      </a>
                    )
                  ))}
                </nav>

                <div className="flex items-center gap-4">
                  {/* Favorites Icon */}
                  <Link to="/favoritos" className="relative group">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative"
                      style={{
                        ['--hover-bg' as any]: `${menuColor}40`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${menuColor}40`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = ``;
                      }}
                      aria-label="Ver favoritos"
                    >
                      <Heart className="h-5 w-5 text-foreground group-hover:text-primary transition-colors"
                        style={{ color: menuColor }}
                      />
                      {favoritesCount > 0 && (
                        <span
                          className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white rounded-full px-1"
                          style={{ backgroundColor: buttonColor }}
                        >
                          {favoritesCount > 99 ? '99+' : favoritesCount}
                        </span>
                      )}
                    </Button>
                  </Link>

                  {/* <ConfigModal /> */}

                  {hasWebBanners && mainService ? (
                    <>
                      <HoverCard open={isHoverCardOpen} onOpenChange={setIsHoverCardOpen} openDelay={200}>
                        <HoverCardTrigger asChild>
                          <div>
                            <Button
                              variant="default"
                              className="hidden md:flex text-white"
                              style={{
                                background: `linear-gradient(to right, ${menuColor}, ${menuColor}dd)`,
                                color: headerColor
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = `linear-gradient(to right, ${menuColor}ee, ${menuColor})`;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = `linear-gradient(to right, ${menuColor}, ${menuColor}dd)`;
                              }}
                            >
                              <MainServiceIcon className="h-4 w-4 mr-2" />
                              {mainServiceTitle}
                            </Button>
                          </div>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-[320px] p-0 overflow-hidden border-0 shadow-2xl" side="bottom" align="end">
                          {mainServiceInfo && (
                            <div className="group">
                              {/* Image Header */}
                              <div className="relative h-30 overflow-hidden">
                                <img
                                  src={mainServiceImage}
                                  alt={mainServiceInfo.title}
                                  className="w-full h-full object-cover"
                                />
                                {/* Gradient Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                                {/* Icon Overlay */}
                                <div className="absolute top-3 right-3">
                                  <div className={`w-10 h-10 ${mainServiceInfo.color} rounded-lg flex items-center justify-center backdrop-blur-sm bg-white/10`}>
                                    <MainServiceIcon className="h-5 w-5 text-white" />
                                  </div>
                                </div>

                                {/* Title Overlay */}
                                <div className="absolute bottom-3 left-3 right-3">
                                  <h3 className="text-white font-bold text-sm leading-tight drop-shadow-lg">
                                    {mainServiceInfo.title}
                                  </h3>
                                </div>
                              </div>

                              {/* Content */}
                              <div className="p-4 space-y-3">
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {mainServiceInfo.description}
                                </p>

                                {/* Action Button - Opens external modal */}
                                <Button
                                  onClick={() => {
                                    setIsHoverCardOpen(false);
                                    setIsServiceModalOpen(true);
                                  }}
                                  className="w-full text-white font-semibold py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                                  style={{
                                    background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                                  }}
                                >
                                  {getServiceButtonText(mainServiceId)}
                                </Button>
                              </div>
                            </div>
                          )}
                        </HoverCardContent>
                      </HoverCard>

                      {/* Service Modal - Outside HoverCard to prevent unmounting */}
                      <ServiceModal
                        service={mainService}
                        config={currentConfig}
                        isOpen={isServiceModalOpen}
                        onOpenChange={setIsServiceModalOpen}
                      />
                    </>
                  ) : (
                    <Link to="/contato">
                      <Button
                        variant="outline"
                        className="hidden md:flex font-semibold hover:bg-accent hover:text-accent-foreground transition-all"
                        style={{
                          color: headerColor,
                          background: menuColor
                        }}
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Contato
                      </Button>
                    </Link>
                  )}

                  {/* Layout Selection Button - Only show for specific accountId */}
                  {shouldShowDevButtons && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="relative hover:bg-white/10"
                        style={{
                          color: menuColor,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${menuColor}20`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = ``;
                        }}
                        onClick={() => setIsLayoutModalOpen(true)}
                        title="Selecionar Layout"
                        aria-label="Selecionar layout"
                      >
                        <Layout className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="relative hover:bg-white/10"
                        style={{
                          color: menuColor,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${menuColor}20`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = ``;
                        }}
                        onClick={() => setIsAccountIdModalOpen(true)}
                        title="Alterar ID da Conta"
                        aria-label="Alterar ID da conta"
                      >
                        <Settings className="h-5 w-5" />
                      </Button>
                    </>
                  )}

                </div>
              </div>

              {/* Mobile Menu Button */}
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden hover:bg-white/10 p-2 rounded-full transition-all"
                    aria-label="Abrir menu"
                  >
                    <Menu className="h-6 w-6" style={{ color: menuColor }} />
                  </Button>
                </SheetTrigger>

                <SheetContent side="right" className="w-full sm:w-full p-0 border-0 [&>button]:hidden">
                  <div className="flex flex-col h-full">
                    {/* Header */}
                    <SheetHeader className="px-4 py-6" style={{ backgroundColor: headerColor }}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {renderBrandMark('sheet')}
                          <div className="text-left min-w-0">
                            <SheetTitle className="text-lg" style={{ color: menuColor }}>{currentConfig.name}</SheetTitle>
                            {currentConfig.slogan && (
                              <p className="text-xs mt-0.5" style={{ color: menuColor, opacity: 0.75 }}>{currentConfig.slogan}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {shouldShowDevButtons && (
                            <>
                              <button
                                onClick={() => {
                                  setIsOpen(false);
                                  setIsLayoutModalOpen(true);
                                }}
                                className="hover:bg-white/15 p-2 rounded-full transition-all"
                                aria-label="Selecionar layout (mobile)"
                                title="Selecionar Layout"
                                style={{ color: menuColor }}
                              >
                                <Layout className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => {
                                  setIsOpen(false);
                                  setIsAccountIdModalOpen(true);
                                }}
                                className="hover:bg-white/15 p-2 rounded-full transition-all"
                                aria-label="Alterar ID da conta (mobile)"
                                title="Alterar ID da Conta"
                                style={{ color: menuColor }}
                              >
                                <Settings className="h-5 w-5" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setIsOpen(false)}
                            className="hover:bg-white/10 p-2 rounded-full transition-all"
                            aria-label="Fechar menu"
                          >
                            <X className="h-5 w-5"
                              style={{ color: menuColor }} />
                          </button>
                        </div>
                      </div>
                    </SheetHeader>

                    {/* Menu Items */}
                    <div className="flex-1 overflow-y-auto py-4" style={{ background: headerColor }}>
                      <nav className="flex flex-col gap-1 px-3">
                        {menuItems.map((item: any, index) => (
                          <div key={item.label}>
                            {item.isModal ? (
                              <button
                                onClick={() => {
                                  setIsOpen(false);
                                  setIsServicesModalOpen(true);
                                }}
                                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/10 transition-all duration-200 group w-full text-left"
                                style={{ color: menuColor }}
                              >
                                <span className="text-sm font-medium">{item.label}</span>
                              </button>
                            ) : item.isLink ? (
                              <Link
                                to={item.href}
                                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/10 transition-all duration-200 group"
                                onClick={() => setIsOpen(false)}
                                style={{ color: menuColor }}
                              >
                                <span className="text-sm font-medium">{item.label}</span>
                              </Link>
                            ) : (
                              <a
                                href={item.href}
                                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/10 transition-all duration-200 group"
                                onClick={() => setIsOpen(false)}
                                style={{ color: menuColor }}
                              >
                                <span className="text-sm font-medium">{item.label}</span>
                              </a>
                            )}
                          </div>
                        ))}
                      </nav>

                      <Separator className="my-4" style={{ background: `${menuColor}10` }} />

                      <div className='flex flex-col gap-1 px-3'>
                        <Link
                          to="/favoritos"
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/10 transition-all duration-200"
                          onClick={() => setIsOpen(false)}
                          style={{ color: menuColor }}
                        >
                          <Heart className="h-5 w-5" style={{ color: menuColor }} />
                          <span className="font-medium">Favoritos</span>
                          {favoritesCount > 0 && (
                            <span
                              className="ml-auto text-xs font-bold text-white px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: buttonColor }}
                            >
                              {favoritesCount}
                            </span>
                          )}
                        </Link>
                      </div>

                      <Separator className="my-4" style={{ background: `${menuColor}10` }} />

                      {/* Store Selector - Mobile */}
                      {showStoreSelector && (
                        <div className='px-3'>
                          <div className="px-4 py-2">
                            <p className="text-xs font-semibold text-muted-foreground mb-2">SELECIONAR LOJA</p>
                          </div>
                          <div className="space-y-1">
                            {availableStores.map((storeOption: any, index: number) => {
                              const distance = storeDistances[storeOption._id] || null;
                              return (
                                <button
                                  key={storeOption._id}
                                  onClick={() => {
                                    handleStoreChange(index);
                                    setIsOpen(false);
                                  }}
                                  className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${selectedStoreIndex === index
                                    ? 'bg-primary/10 border-l-2 font-medium'
                                    : 'hover:bg-accent/10'
                                    }`}
                                  style={selectedStoreIndex === index
                                    ? { borderLeftColor: menuColor, color: menuColor }
                                    : { color: menuColor }
                                  }
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <MapPin className="h-4 w-4 flex-shrink-0" />
                                      <div className="flex flex-col">
                                        <span className="text-sm">{storeOption.company}</span>
                                        {distance !== null && (
                                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Navigation className="h-3 w-3" />
                                            {formatDistance(distance)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {selectedStoreIndex === index && (
                                      <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: menuColor }} />
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer Action */}
                    {hasWebBanners && mainService && (
                      <div className="p-4 m-0" style={{ background: headerColor, border: `2px solid ${headerColor}` }}>
                        <ServiceModal service={mainService} config={currentConfig}>
                          <Button
                            className="w-full text-white font-semibold shadow-lg"
                            size="lg"
                            style={{
                              background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                            }}
                          >
                            <MainServiceIcon className="h-5 w-5 mr-2" />
                            {mainServiceTitle}
                          </Button>
                        </ServiceModal>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      {/* Store Selection Modal */}
      {showStoreSelector && (
        <StoreSelectionModal
          config={currentConfig}
          isOpen={isStoreModalOpen}
          onClose={() => setIsStoreModalOpen(false)}
        />
      )}

      {/* Layout Selection Modal */}
      {shouldShowDevButtons && (
        <LayoutSelectionModal
          isOpen={isLayoutModalOpen}
          onOpenChange={setIsLayoutModalOpen}
        />
      )}

      {/* Account ID Modal */}
      {shouldShowDevButtons && (
        <AccountIdModal
          isOpen={isAccountIdModalOpen}
          onOpenChange={setIsAccountIdModalOpen}
        />
      )}

      {/* Services Modal */}
      <Dialog open={isServicesModalOpen} onOpenChange={setIsServicesModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col gap-0">
          {/* Fixed Header */}
          <div className="sticky top-0 z-10 bg-background border-b px-6 py-4">
            <h2 className="text-2xl font-bold text-foreground">Nossos Serviços</h2>
            <p className="text-muted-foreground mt-1">Conheça todos os serviços que oferecemos</p>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {store.services?.map((service: any) => {
                const info = serviceInfo[service.service];
                if (!info) return null;

                const ServiceIcon = info.icon;

                return (
                  <ServiceModal key={service.service} service={service} config={currentConfig}>
                    <button className="w-full text-left group">
                      <div className="border border-border/50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-primary/50">
                        {/* Service Image */}
                        <div className="relative h-32 overflow-hidden bg-gradient-to-br from-muted to-muted/50">
                          <img
                            src={service.image || info.image}
                            alt={info.title}
                            className="w-full h-full object-cover transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute top-2 right-2">
                            <div className={`w-10 h-10 ${info.color} rounded-lg flex items-center justify-center backdrop-blur-sm bg-white/10`}>
                              <ServiceIcon className="h-5 w-5 text-white" />
                            </div>
                          </div>
                        </div>

                        {/* Service Info */}
                        <div className="p-4">
                          <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                            {info.title}
                          </h3>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {info.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  </ServiceModal>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};
